import { useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, Plus, Pencil, RotateCcw, X, FolderPlus } from 'lucide-react'
import { api, upload } from '../../lib/api.js'
import { toast } from '../../components/feedback/Toast.jsx'
import Carousel from '../../components/Carousel.jsx'
import { fmtDay, useCanEdit, canTouch, primeKeyboard, deliveryHref } from './lib.js'
import BackBar from './BackBar.jsx'
import SessionFiles from './SessionFiles.jsx'

/**
 * One session entry: mics, signal chain, photos, notes. "Recall this setup"
 * turns any past session into the starting point for a new one — the whole
 * point of the product.
 */
export default function SessionPage() {
  const { projectId, sessionId } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: () => api('/auth/me'), staleTime: Infinity })
  const { data, isLoading } = useQuery({
    queryKey: ['sl-session', sessionId],
    queryFn: () => api(`/log/sessions/${sessionId}`),
  })
  const canEdit = useCanEdit(me)
  const fileRef = useRef(null)
  const [uploading, setUploading] = useState(null) // {done, total} while photos are going up
  const [viewer, setViewer] = useState(null) // index of the photo open in the carousel

  if (isLoading) return <p className="text-sm text-muted">Loading…</p>
  if (!data?.session) return <p className="text-sm text-muted">No such session.</p>
  const s = data.session
  // Recall is for everyone signed in; revising THIS entry (edit, delete,
  // photos, convert) needs editor+ or being its creator — guest tier.
  const canModify = canTouch(me, s)
  // Standalone sessions (no project) navigate back to the Session Log home.
  const homePath = s.projectId ? `/log/${s.projectId}` : '/log'
  const newFromPath = s.projectId ? `/log/${s.projectId}/new?from=${s.id}` : `/log/new?from=${s.id}`

  // Several photos at once: the picker is `multiple`, and they go up one
  // request at a time because multipart is registered with files: 1 and the
  // position column is a MAX(position)+1 read — parallel uploads would race
  // it. A failure names the photo that didn't make it; the rest carry on.
  async function addPhotos(e) {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    if (files.length === 0) return
    setUploading({ done: 0, total: files.length })
    let added = 0
    for (const file of files) {
      try {
        await upload(`/log/sessions/${s.id}/media`, file)
        added += 1
      } catch (err) {
        toast(`“${file.name}” didn’t upload: ${err.message || 'error'}`, 'error')
      }
      setUploading((u) => ({ ...u, done: u.done + 1 }))
    }
    setUploading(null)
    if (added > 0) {
      qc.invalidateQueries({ queryKey: ['sl-session', sessionId] })
      qc.invalidateQueries({ queryKey: ['sl-project', projectId] })
    }
  }

  async function removePhoto(mediaId) {
    if (!window.confirm('Remove this photo?')) return
    try {
      await api(`/log/sessions/${s.id}/media/${mediaId}`, { method: 'DELETE' })
      qc.invalidateQueries({ queryKey: ['sl-session', sessionId] })
    } catch (err) {
      toast(err.message || 'Couldn’t remove photo', 'error')
    }
  }

  async function convertToProject() {
    // The name becomes the PROJECT (the container); this entry is renamed
    // "Day 1" server-side, like every day after it (Charles, 2026-08-29).
    const name = window.prompt('Multi-day session name — this entry becomes Day 1:', s.label || '')
    if (!name?.trim()) return
    try {
      const res = await api(`/log/sessions/${s.id}/convert`, { body: { name: name.trim() } })
      qc.invalidateQueries({ queryKey: ['sl-projects'] })
      qc.invalidateQueries({ queryKey: ['sl-session', sessionId] })
      navigate(`/log/${res.projectId}`)
    } catch (err) {
      toast(err.message || 'Couldn’t convert', 'error')
    }
  }

  async function removeSession() {
    if (!window.confirm('Delete this session? (Soft delete — recoverable from the database.)')) return
    try {
      await api(`/log/sessions/${s.id}`, { method: 'DELETE' })
      if (projectId) qc.invalidateQueries({ queryKey: ['sl-project', projectId] })
      qc.invalidateQueries({ queryKey: ['sl-projects'] })
      navigate(homePath)
    } catch (err) {
      toast(err.message || 'Couldn’t delete', 'error')
    }
  }

  return (
    <div>
      <BackBar to={homePath} label={s.projectName || 'Session Log'} />
      {s.projectId && <SiblingDays projectId={s.projectId} currentId={s.id} />}

      <div className="mb-4 border-b border-hairline pb-3">
        <h1 className="text-2xl font-semibold text-ink">{s.label || 'Session'}</h1>
        <div className="mt-1 text-sm text-muted">
          {fmtDay(s.date)}
          {s.by ? ` · logged by ${s.by}` : ''}
        </div>
        {(s.room || s.sessionType || s.sampleRate || s.bitDepth) && (
          <div className="mt-1 text-sm font-medium text-ink">
            {[
              s.room,
              s.sessionType === 'Other' ? s.sessionTypeOther || 'Other' : s.sessionType,
              s.sampleRate,
              s.bitDepth,
            ].filter(Boolean).join(' · ')}
          </div>
        )}
        {s.narrators && (
          <div className="mt-1 text-sm text-muted">
            Narrator{s.narrators.includes(',') ? 's' : ''}: <span className="text-ink">{s.narrators}</span>
          </div>
        )}
        {s.socialTitle && (
          <div className="mt-1 text-sm text-muted">
            Book / show / film: <span className="text-ink">{s.socialTitle}</span>
          </div>
        )}
        {s.notableTalent && (
          <div className="mt-1 text-sm text-muted">
            Notable talent: <span className="text-ink">{s.notableTalent}</span>
          </div>
        )}
        {s.editGoesTo && (
          <div className="mt-1 text-sm text-muted">
            Edit goes to:{' '}
            {deliveryHref(s.editGoesTo) ? (
              <a href={deliveryHref(s.editGoesTo)} className="text-link underline underline-offset-2">
                {s.editGoesTo}
              </a>
            ) : (
              <span className="text-ink">{s.editGoesTo}</span>
            )}
          </div>
        )}
        {s.leftOffPage && (
          <div className="mt-1 text-sm text-muted">
            Left off on page: <span className="font-semibold text-ink">{s.leftOffPage}</span>
          </div>
        )}
        {s.lunchCost && (
          <div className="mt-1 text-sm text-muted">
            Lunch: <span className="font-semibold text-ink">{s.lunchCost}</span>
          </div>
        )}
        {s.recalledFrom && (
          <div className="mt-1 text-xs text-faint">
            setup from{' '}
            <Link
              to={s.projectId ? `/log/${s.projectId}/s/${s.recalledFrom.id}` : `/log/s/${s.recalledFrom.id}`}
              className="underline underline-offset-2"
            >
              {s.recalledFrom.label || fmtDay(s.recalledFrom.date)}
            </Link>
          </div>
        )}
      </div>

      {canEdit && (
        <div className="mb-3 flex gap-2">
          <button
            onClick={() => { primeKeyboard(); navigate(newFromPath) }}
            className="tap flex flex-1 items-center justify-center gap-2 rounded-xl bg-navy px-4 py-2.5 text-sm font-medium text-white"
          >
            <RotateCcw size={15} /> New session from this setup
          </button>
          {canModify && (
            <button
              onClick={() => navigate(s.projectId ? `/log/${s.projectId}/s/${s.id}/edit` : `/log/s/${s.id}/edit`)}
              className="tap flex items-center justify-center gap-1.5 rounded-xl border-ink px-4 py-2.5 text-sm font-medium text-ink"
              style={{ borderWidth: '1.5px' }}
            >
              <Pencil size={14} /> Edit
            </button>
          )}
        </div>
      )}

      {canModify && !s.projectId && (
        <button
          onClick={convertToProject}
          className="tap mb-5 flex w-full items-center justify-center gap-2 rounded-xl border border-hairline bg-white px-4 py-2.5 text-sm font-medium text-ink active:bg-grouped"
        >
          <FolderPlus size={15} /> Turn into a multi-day session
        </button>
      )}

      <div className="space-y-5">
        <SessionFiles sessionId={s.id} date={s.date} />

        {s.gear.length > 0 && (
          <section>
            <h2 className="section-header mb-2">Mic(s) · {s.gear.length}</h2>
            <div className="card divide-y divide-hairline">
              {s.gear.map((g, i) => (
                <div key={i} className="px-4 py-2.5">
                  <div className="text-sm font-medium text-ink">{g.name}</div>
                  {g.settings && <div className="mt-0.5 text-xs text-muted">{g.settings}</div>}
                </div>
              ))}
            </div>
          </section>
        )}

        {s.chain.length > 0 && (
          <section>
            <h2 className="section-header mb-2">Signal chain</h2>
            <div className="card flex flex-wrap items-center gap-2 px-4 py-3">
              {s.chain.map((node, i) => (
                <span key={i} className="flex items-center gap-2">
                  <span className="bg-grouped px-2 py-1 text-xs font-medium text-ink">{node}</span>
                  {i < s.chain.length - 1 && <ArrowRight size={12} className="text-faint" />}
                </span>
              ))}
            </div>
          </section>
        )}

        <section>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="section-header">Photos · {s.photos.length}</h2>
            {canModify && (
              <button
                onClick={() => fileRef.current?.click()}
                disabled={!!uploading}
                className="tap flex items-center gap-1 text-xs font-medium text-ink underline underline-offset-2 disabled:opacity-50"
              >
                <Plus size={13} />{' '}
                {uploading
                  ? uploading.total > 1
                    ? `Uploading ${Math.min(uploading.done + 1, uploading.total)} of ${uploading.total}…`
                    : 'Uploading…'
                  : 'Add photos'}
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={addPhotos} className="hidden" />
          {s.photos.length === 0 ? (
            <p className="text-xs text-faint">Mic positions, console shots, room layouts…</p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {s.photos.map((p, i) => (
                <Photo
                  key={p.id}
                  photo={p}
                  sessionId={s.id}
                  canEdit={canModify}
                  onRemove={removePhoto}
                  onView={() => setViewer(i)}
                />
              ))}
            </div>
          )}
        </section>

        {s.notes && (
          <section>
            <h2 className="section-header mb-2">Notes</h2>
            <div className="card px-4 py-3">
              <p className="whitespace-pre-wrap font-serif text-[15px] leading-relaxed text-ink">{s.notes}</p>
            </div>
          </section>
        )}
      </div>

      <div className="mt-10 space-y-3 text-center">
        {canModify && (
          <div>
            <button onClick={removeSession} className="tap text-xs text-danger underline underline-offset-2">
              Delete session
            </button>
          </div>
        )}
        <div>
          <Link to={homePath} className="tap inline-block text-sm font-medium text-ink underline underline-offset-2">
            Close
          </Link>
        </div>
      </div>

      {viewer !== null && (
        <Carousel photos={s.photos} start={viewer} onClose={() => setViewer(null)} />
      )}
    </div>
  )
}

/** Thumb + inline caption ("the tag") — saves on blur, no taxonomy. */
function Photo({ photo, sessionId, canEdit, onRemove, onView }) {
  const [caption, setCaption] = useState(photo.caption)

  async function saveCaption() {
    if (caption.trim() === (photo.caption || '')) return
    try {
      await api(`/log/sessions/${sessionId}/media/${photo.id}`, {
        method: 'PATCH',
        body: { caption: caption.trim() },
      })
    } catch (err) {
      toast(err.message || 'Couldn’t save caption', 'error')
    }
  }

  return (
    <figure className="relative">
      <button type="button" onClick={onView} className="block w-full" aria-label="View photo">
        <img src={photo.thumb} alt={photo.caption || ''} className="aspect-square w-full border border-hairline object-cover" />
      </button>
      {canEdit && (
        <button
          onClick={() => onRemove(photo.id)}
          className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center bg-ink/80 text-white"
          aria-label="Remove photo"
        >
          <X size={13} />
        </button>
      )}
      {canEdit ? (
        <input
          type="text"
          placeholder="Tag it — e.g. vocal mic"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          onBlur={saveCaption}
          className="mt-1 w-full border-0 border-b border-hairline bg-transparent px-0.5 py-1 text-xs text-ink placeholder:text-faint focus:border-ink focus:outline-none"
        />
      ) : (
        photo.caption && <figcaption className="mt-1 px-0.5 text-xs text-muted">{photo.caption}</figcaption>
      )}
    </figure>
  )
}


/**
 * The other days of a multi-day session, one tap away (Charles,
 * 2026-10-04: "make it possible to tap or click on the day you're not
 * looking at to be able to get to it easily"). Reads the project's own
 * cached query, so it costs nothing extra on the way in from the project.
 */
function SiblingDays({ projectId, currentId }) {
  const q = useQuery({ queryKey: ['sl-project', String(projectId)], queryFn: () => api(`/log/projects/${projectId}`), staleTime: 60 * 1000 })
  const days = q.data?.sessions || []
  if (days.length < 2) return null
  return (
    <div className="-mt-1 mb-3 flex flex-wrap gap-1.5" aria-label="Days of this session">
      {days.map((d, i) => {
        const on = d.id === currentId
        return on ? (
          <span key={d.id} className="rounded-full bg-navy px-2.5 py-0.5 text-xs font-medium text-white">{d.label || `Day ${i + 1}`}</span>
        ) : (
          <Link key={d.id} to={`/log/${projectId}/s/${d.id}`} className="tap rounded-full border border-hairline bg-white px-2.5 py-0.5 text-xs font-medium text-ink active:bg-grouped">{d.label || `Day ${i + 1}`}</Link>
        )
      })}
    </div>
  )
}
